import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { ChevronLeft, ChevronRight } from '@preqin/ui-components/cjs/icons/navigation';

import styles from './SideNav.module.scss';

const ToggleButton = ({ isVisible, onToggle }: { isVisible: boolean; onToggle: () => void }) => {
  return (
    <button onClick={onToggle} className={styles.sideNav__showHideButton}>
      <div className={styles.sideNav__showHideButton__chevron}>
        {isVisible ? <ChevronLeft height="16px" width="16px" /> : <ChevronRight height="16px" width="16px" />}
      </div>
    </button>
  );
};

export const SideNav = ({ children, visible }: { children: React.ReactNode; visible: boolean }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const [isSideNavVisible, setIsSideNavVisible] = useState<boolean>(false);
  useEffect(() => {
    const handleScroll = (containerRef: HTMLDivElement) => () => {
      containerRef.dataset.scroll = containerRef.scrollTop.toString();
    };
    navRef.current?.addEventListener('scroll', handleScroll(navRef.current), { passive: true });
    return () => navRef.current?.removeEventListener('scroll', handleScroll(navRef.current));
  }, []);

  const onNavToggle = () => {
    setIsSideNavVisible(!isSideNavVisible);
    setTimeout(() => window.dispatchEvent(new Event('resize')), 100); //required to resize the table on searchFor so that it does not crop on show/hide of nav
  };

  const sideNavClasses = useMemo(
    () =>
      classNames(styles.sideNav, {
        [styles.sideNav__open]: isSideNavVisible,
        [styles.sideNav__close]: !isSideNavVisible,
      }),
    [isSideNavVisible],
  );

  const contentWrapperClasses = useMemo(
    () =>
      classNames({
        [styles.sideNav__hideContent]: !isSideNavVisible,
      }),
    [isSideNavVisible],
  );

  const scrollClasses = useMemo(
    () =>
      classNames({
        [styles.sideNav__scroll]: isSideNavVisible,
      }),
    [isSideNavVisible],
  );

  return visible ? (
    <nav className={sideNavClasses}>
      <div className={contentWrapperClasses} role="menu" aria-hidden={!isSideNavVisible}>
        <div ref={navRef} data-scroll="0" className={scrollClasses}>
          {children}
        </div>
      </div>
      <ToggleButton isVisible={isSideNavVisible} onToggle={onNavToggle} />
    </nav>
  ) : null;
};
